/**
 * helpRoutes — curated helpKey → route map for the Help Center hub.
 *
 * The help registry (helpContent.tsx) has no route column and the command
 * palette has no helpKey column, so this is the explicit join. Paths are
 * sourced from the palette catalog (CommandPalette buildCatalog) — the
 * trusted route list. Coverage is intentionally partial: an entry with no
 * mapping still appears in the Help Center (read-only card), it just loses
 * the "Open the page" button. Add mappings as pages earn them.
 */
export const HELP_ROUTES: Record<string, string> = {
  // Organization
  'org-hierarchy': '/organizations',

  // Workflow management + forms
  'wfm-overview': '/workflow-management/overview',
  'wfm-requests': '/workflow-management/requests',
  'wfm-delegations': '/workflow-management/delegations',
  'wfm-runs': '/workflow-management/runs',
  'wfm-templates': '/workflow-management/templates',
  'wfm-libraries': '/workflow-management/libraries',
  'wfm-document-control': '/workflow-management/document-control',
  'wfm-groups': '/workflow-management/groups',
  'wfm-ai-connection': '/workflow-management/ai-connection',
  'wfm-access': '/workflow-management/access',
  'wfm-directory': '/workflow-management/directory',
  'wfm-email': '/workflow-management/email',
  'wfm-sites': '/workflow-management/sites',
  'wfm-routing-matrix': '/workflow-management/routing-matrix',
  'wfm-teams': '/workflow-management/teams',
  'forms-my-submissions': '/workflow-management/my-submissions',
  'wfm-training': '/workflow-management/training',
  'wfm-training-compliance': '/workflow-management/training/compliance',
  'wfm-training-setup': '/workflow-management/training/setup',
  'wfm-reports': '/workflow-management/reports',
  'wfm-report-schedules': '/workflow-management/reports',
  'wfm-dashboards': '/workflow-management/dashboards',
  'wfm-landing-pages': '/workflow-management/pages',
  'wfm-request-register': '/workflow-management/reports/request-register',
  'wfm-search': '/workflow-management/search',

  // Close-out sweep 2026-07-25
  'skill-approvals': '/skill-approvals',
  'config-client-setup': '/config/client-setup',
  'config-access': '/config/access',
  'config-resource-planning': '/config/resource-planning',
  'portfolio-new': '/portfolios/new',
  'api-explorer': '/developer/api-explorer',

  // Portfolio
  'portfolio-dashboard': '/dashboard',
  'budget-releases': '/budget-releases',
  'scenario-planning': '/scenario-planning',
  'resource-consumption': '/resource-consumption',
  'project-list': '/projects',
  'project-requests': '/project-requests',
  'priorities': '/priorities',
  'workflows': '/workflows',
  'analysis': '/analysis',
  'risks': '/risks',
  'milestone-analyzer': '/milestone-analyzer',
  'program-review': '/programs/review',

  // Project
  'gantt': '/gantt',
  'finance': '/finance',
  'forecasts': '/forecasts',
  'evm': '/evm',
  'wbs-hierarchy': '/hierarchy',
  'reports': '/reports',

  // Contracts (CLM)
  'contracts-dashboard': '/contracts',
  'contracts-list': '/contracts/all',
  'clm-approval-inbox': '/contracts/approvals',
  'contracts-templates': '/contracts/templates',
  'clm-preferred-terms': '/contracts/preferred-terms',
  'contracts-customer-analysis': '/contracts/customers',
  'clm-workflow-analytics': '/contracts/analytics',

  // Supply chain
  'supply-chain-home': '/supply-chain',
  'supply-chain-demand': '/supply-chain/demand',
  'supply-chain-supply': '/supply-chain/supply',
  'supply-chain-inventory': '/supply-chain/inventory',
  'supply-chain-capacity': '/supply-chain/capacity',
  'supply-chain-gpd': '/supply-chain/gpd',
  'supply-chain-promising': '/supply-chain/promising',
  'supply-chain-response': '/supply-chain/response',
  'supply-chain-control-tower': '/supply-chain/control-tower',
  'supply-chain-supplier': '/supply-chain/supplier',
  'supply-chain-compliance': '/supply-chain/compliance',
  'supply-chain-sustainment': '/supply-chain/sustainment',
  'supply-chain-sop': '/supply-chain/sop',

  // Timesheet (main surfaces)
  'ts-dashboard': '/timesheet',
  'ts-my-timesheet': '/timesheet/my',
  'ts-approvals-queue': '/timesheet/approvals',
  'ts-my-requests': '/timesheet/my-requests',
  'ts-pto-requests': '/timesheet/pto-requests',
  'ts-settings': '/timesheet/settings',
  'ts-timesheet-admin-hub': '/timesheet/admin',
  'ts-floor-check': '/timesheet/floor-check',
  'ts-reports': '/timesheet/reports',

  // Config / platform
  'config-users': '/config/users',
  'config-roles': '/config/roles',
  'config-policies': '/config/policies',
  'config-audit': '/config/audit',
  'config-ui-mappings': '/config/ui-mappings',
  'config-project-lifecycle': '/config/project-lifecycle',
  'notifications': '/notifications',
  'my-work': '/my-work',
}

/** Module bucket for a helpKey — drives the Help Center's grouping chips. */
export function helpModuleFor(helpKey: string): string {
  if (helpKey.startsWith('ts-')) return 'Timesheet'
  if (helpKey.startsWith('supply-chain-')) return 'Supply Chain'
  if (helpKey.startsWith('clm-') || helpKey.startsWith('contracts-')) return 'Contracts'
  if (helpKey.startsWith('config-') || helpKey.startsWith('org-')) return 'Organization & Config'
  if (['gantt', 'finance', 'forecasts', 'evm', 'wbs-hierarchy', 'hierarchy-linkages', 'project-detail', 'project-team', 'project-attributes', 'project-locations', 'lessons-learned', 'implementation-delivery', 'project-startup-checklist', 'erp-sync', 'project-fx', 'estimate-import', 'rates', 'materials'].includes(helpKey)) return 'Project'
  if (['reports', 'notifications', 'my-work', 'workflows', 'workflows-v3', 'help-center'].includes(helpKey)) return 'Platform'
  return 'Portfolio'
}
